import React, {
  Component,
  PropsWithChildren,
  ReactNode,
} from 'react';
import isNil from 'lodash/isNil';
import ContentPasteOffOutlinedIcon from '@mui/icons-material/ContentPasteOffOutlined';
import {
  Button,
} from '@populateui/ui';
import {
  getNoAccessTitle,
  isNoAccessError,
  NO_ACCESS_DETAIL,
} from 'utils/errors';

type UnavailableProps = {
  // Shown only when the panel was opened on top of another
  backButton?: ReactNode;
  // Such as "to-do"; defaults to "record"
  recordName?: string;
  onClose: () => void;
};

function PanelUnavailable({ backButton, recordName, onClose }: UnavailableProps) {
  return (
    <div className="flex flex-col bg-white rounded-xl h-full w-[640px] max-w-full">
      {!isNil(backButton) && (
        <div className="flex items-center px-4 pt-4">
          {backButton}
        </div>
      )}
      <div className="flex flex-1 flex-col items-center justify-center gap-6 px-10 pb-20 text-center">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 text-blue-600">
          <ContentPasteOffOutlinedIcon className="!w-8 !h-8" />
        </div>
        <div className="flex flex-col gap-2 max-w-sm">
          <h2 className="text-lg font-semibold text-gray-900">
            {getNoAccessTitle(recordName)}
          </h2>
          <p className="text-sm text-gray-500 whitespace-nowrap">
            {NO_ACCESS_DETAIL}
          </p>
        </div>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

type Props = PropsWithChildren<UnavailableProps & {
  // The shown panel; a new one clears the error
  resetKey: string;
  // Resets errored queries, so reopening the record asks the server again
  onReset: () => void;
}>;

type State = {
  error: unknown;
  resetKey: string;
};

// Shows a "not available" panel for 403/404, and passes other errors up
export default class PanelErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, resetKey: props.resetKey };
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    return props.resetKey === state.resetKey ? null : { error: null, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  // Resets right away, so the next panel's queries fetch again instead of rethrowing
  componentDidCatch() {
    const { onReset } = this.props;
    onReset();
  }

  render() {
    const {
      children,
      backButton,
      recordName,
      onClose,
    } = this.props;
    const { error } = this.state;
    const hasError = !isNil(error);

    if (hasError && !isNoAccessError(error)) {
      throw error;
    }

    return hasError
      ? <PanelUnavailable backButton={backButton} recordName={recordName} onClose={onClose} />
      : children;
  }
}
